import React, { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetTitle, SheetDescription } from "@/components/ui/sheet";

const LINKS = [
    { href: "#about", label: "About" },
    { href: "#team", label: "Team" },
    { href: "#projects", label: "Projects" },
    { href: "#capabilities", label: "Capabilities" },
    { href: "#contact", label: "Contact" },
];

export const Navigation = () => {
    const [scrolled, setScrolled] = useState(false);
    const [active, setActive] = useState("");
    const [open, setOpen] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });

        // active section observer
        const ids = LINKS.map((l) => l.href.slice(1));
        const elements = ids
            .map((id) => document.getElementById(id))
            .filter(Boolean);
        const obs = new IntersectionObserver(
            (entries) => {
                entries.forEach((e) => {
                    if (e.isIntersecting) setActive("#" + e.target.id);
                });
            },
            { rootMargin: "-40% 0px -55% 0px", threshold: 0 }
        );
        elements.forEach((el) => obs.observe(el));

        return () => {
            window.removeEventListener("scroll", onScroll);
            obs.disconnect();
        };
    }, []);

    return (
        <header
            data-testid="top-nav"
            className="fixed top-0 left-0 right-0 z-50 bg-black border-b border-white/10"
        >
            <nav className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
                <a
                    href="#hero"
                    data-testid="nav-logo"
                    aria-label="Studio Shakthi \u2014 Home"
                    className="flex items-center gap-3 group"
                >
                    <img
                        src="/brand/ssppl-logo.png"
                        alt="Studio Shakthi"
                        className="h-[2.7rem] sm:h-12 w-auto object-contain select-none"
                        draggable={false}
                    />
                </a>

                {/* Desktop nav */}
                <ul className="hidden md:flex items-center gap-8">
                    {LINKS.map((l) => (
                        <li key={l.href}>
                            <a
                                href={l.href}
                                data-testid={`nav-link-${l.href.slice(1)}`}
                                className={`cine-underline font-mono text-[11px] uppercase tracking-[0.18em] text-[hsl(var(--text-secondary))] hover:text-white transition-colors ${
                                    active === l.href ? "is-active text-white" : ""
                                }`}
                            >
                                {l.label}
                            </a>
                        </li>
                    ))}
                </ul>

                {/* Mobile */}
                <div className="md:hidden">
                    <Sheet open={open} onOpenChange={setOpen}>
                        <SheetTrigger asChild>
                            <button
                                aria-label="Open menu"
                                data-testid="mobile-nav-open-button"
                                className="p-2 rounded-md hover:bg-white/5"
                            >
                                <Menu className="h-5 w-5" />
                            </button>
                        </SheetTrigger>
                        <SheetContent
                            side="right"
                            data-testid="mobile-nav-sheet"
                            className="bg-[hsl(var(--bg-base))] border-l border-white/10 w-[78vw] sm:w-[420px]"
                        >
                            <SheetTitle className="sr-only">Studio Shakthi navigation</SheetTitle>
                            <SheetDescription className="sr-only">
                                Site navigation menu
                            </SheetDescription>
                            <div className="flex flex-col h-full pt-6">
                                <img
                                    src="/brand/ssppl-logo.png"
                                    alt="Studio Shakthi"
                                    className="h-12 w-auto object-contain mb-10"
                                />
                                <ul className="flex flex-col gap-6">
                                    {LINKS.map((l) => (
                                        <li key={l.href}>
                                            <a
                                                href={l.href}
                                                onClick={() => setOpen(false)}
                                                data-testid={`mobile-nav-link-${l.href.slice(1)}`}
                                                className="font-display text-3xl tracking-wide uppercase hover:text-[hsl(var(--accent-gold))] transition-colors"
                                            >
                                                {l.label}
                                            </a>
                                        </li>
                                    ))}
                                </ul>
                                <div className="mt-auto pb-10">
                                    <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--text-muted))]">
                                        Chennai · India
                                    </span>
                                </div>
                            </div>
                        </SheetContent>
                    </Sheet>
                </div>
            </nav>
        </header>
    );
};
